
/* =======================================================================
   Guffee FX engine
   A self-contained enhancement layer. It runs on the static markup and,
   because the app is powered by a framework that re-renders #guRoot once
   React loads, it re-binds itself via a MutationObserver so nothing gets
   lost across renders. All expensive overlays are appended to <body>
   (outside the app tree) so they never fight the framework's reconciler.
   ======================================================================= */
(function () {
  "use strict";
  var RM = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var FINE = matchMedia("(pointer:fine)").matches && window.innerWidth > 760;
  var done = new WeakSet();      // reveal elements already wired
  var io = null;                 // shared IntersectionObserver
  var parEls = [];               // live parallax targets
  var scrollBound = false;
  var staged = false;            // true once the "show" has opened the hero
  var scene3d = [];              // sections that tilt/recede in 3D with scroll
  var velY = 0, lastY = 0;       // scroll velocity (marquee skew)

  /* ---------------------------------------------------------------- overlays */
  function buildOverlays() {
    if (document.getElementById("gu-aurora")) return;

    var au = document.createElement("div");
    au.className = "gu-aurora";
    au.id = "gu-aurora";
    au.innerHTML =
      '<span class="gu-a1"></span><span class="gu-a2"></span>' +
      '<span class="gu-a3"></span><span class="gu-a4"></span>';
    document.body.appendChild(au);

    // Stage lighting: a soft pool of light at centre + a framing vignette,
    // so scrolling content feels lit and "on stage".
    var spot = document.createElement("div"); spot.className = "gu-spot";
    document.body.appendChild(spot);
    var vg = document.createElement("div"); vg.className = "gu-vignette";
    document.body.appendChild(vg);

    var grain = document.createElement("div");
    grain.className = "gu-grain";
    document.body.appendChild(grain);

    var pr = document.createElement("div");
    pr.className = "gu-progress";
    pr.id = "gu-progress";
    document.body.appendChild(pr);

    if (!RM && window.innerWidth > 760) buildParticles();
    if (FINE && !RM) buildCursor();
  }

  /* ---- drifting energy particles ---- */
  function buildParticles() {
    var c = document.createElement("canvas");
    c.className = "gu-particles";
    document.body.appendChild(c);
    var ctx = c.getContext("2d");
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W, H, ps = [];
    var cols = [[255, 138, 30], [225, 24, 94], [123, 47, 242], [122, 160, 255]];

    function resize() {
      W = c.width = window.innerWidth * dpr;
      H = c.height = window.innerHeight * dpr;
      c.style.width = window.innerWidth + "px";
      c.style.height = window.innerHeight + "px";
    }
    resize();
    window.addEventListener("resize", resize, { passive: true });

    var N = Math.min(64, Math.round(window.innerWidth / 24));
    for (var i = 0; i < N; i++) {
      ps.push({
        x: Math.random() * W, y: Math.random() * H,
        r: (Math.random() * 2.4 + 0.7) * dpr,
        sp: (Math.random() * 0.35 + 0.1) * dpr,
        w: Math.random() * Math.PI * 2, ws: Math.random() * 0.02 + 0.005,
        a: Math.random() * 0.5 + 0.18, c: cols[i % cols.length]
      });
    }
    (function loop() {
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = "lighter";
      for (var i = 0; i < ps.length; i++) {
        var p = ps[i];
        p.y -= p.sp; p.w += p.ws; p.x += Math.sin(p.w) * 0.35 * dpr;
        if (p.y < -30) { p.y = H + 30; p.x = Math.random() * W; }
        var R = p.r * 6;
        var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, R);
        g.addColorStop(0, "rgba(" + p.c[0] + "," + p.c[1] + "," + p.c[2] + "," + p.a + ")");
        g.addColorStop(1, "rgba(" + p.c[0] + "," + p.c[1] + "," + p.c[2] + ",0)");
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(p.x, p.y, R, 0, 7); ctx.fill();
      }
      requestAnimationFrame(loop);
    })();
  }

  /* ---- soft light cursor + dot ---- */
  function buildCursor() {
    var glow = document.createElement("div"); glow.className = "gu-cursor";
    var dot = document.createElement("div"); dot.className = "gu-cursor-dot";
    document.body.appendChild(glow); document.body.appendChild(dot);
    var mx = window.innerWidth / 2, my = window.innerHeight / 2, gx = mx, gy = my;
    window.addEventListener("mousemove", function (e) {
      mx = e.clientX; my = e.clientY;
      dot.style.transform = "translate(" + mx + "px," + my + "px)";
    }, { passive: true });
    (function lerp() {
      gx += (mx - gx) * 0.14; gy += (my - gy) * 0.14;
      glow.style.transform = "translate(" + gx + "px," + gy + "px)";
      requestAnimationFrame(lerp);
    })();
  }

  /* ------------------------------------------------- scroll: progress + parallax */
  function bindScroll() {
    if (scrollBound) return;
    scrollBound = true;
    var doc = document.documentElement, ticking = false;
    function update() {
      var pr = document.getElementById("gu-progress");
      if (pr) {
        var max = doc.scrollHeight - window.innerHeight;
        pr.style.width = (max > 0 ? (window.scrollY / max) * 100 : 0) + "%";
      }
      for (var i = 0; i < parEls.length; i++) {
        var el = parEls[i];
        var f = parseFloat(el.getAttribute("data-parallax")) || 0.15;
        // images are scaled up slightly so the parallax shift never exposes an edge
        var sc = el.tagName === "IMG" ? " scale(1.05)" : "";
        el.style.transform = "translate3d(0," + (window.scrollY * f).toFixed(1) + "px,0)" + sc;
      }
      // --- 3D depth scroll: sections tilt & recede around the viewport centre ---
      var vh = window.innerHeight;
      for (var k = 0; k < scene3d.length; k++) {
        var s3 = scene3d[k], r3 = s3.getBoundingClientRect();
        if (r3.bottom < -120 || r3.top > vh + 120) {
          if (s3.dataset.gu3d) { s3.style.transform = ""; delete s3.dataset.gu3d; }
          continue;
        }
        var pC = (r3.top + r3.height / 2 - vh / 2) / (vh * 0.95);
        pC = Math.max(-1, Math.min(1, pC));
        s3.dataset.gu3d = "1";
        s3.style.transform =
          "perspective(1400px) rotateX(" + (pC * 7.5).toFixed(2) + "deg)" +
          " translateZ(" + (-Math.abs(pC) * 150).toFixed(1) + "px)" +
          " scale(" + (1 - Math.abs(pC) * 0.03).toFixed(4) + ")";
      }
      ticking = false;
    }
    // scroll-velocity skew on the marquee band (decays back to 0 when idle).
    // Time-normalised so the decay feels the same at any frame rate.
    if (!RM && window.innerWidth > 760) { var skPrev = 0; (function skewLoop(ts) {
      var dt = skPrev ? Math.min(4, (ts - skPrev) / 16.7) : 1;
      skPrev = ts || 0;
      var y = window.scrollY;
      var a = 1 - Math.pow(0.82, dt);
      velY += ((y - lastY) - velY) * a;
      lastY = y;
      if (Math.abs(velY) < 0.5) velY = 0;
      var mq = document.querySelector("[data-marquee]");
      if (mq) {
        var sk = Math.max(-4, Math.min(4, -velY * 0.07));
        mq.style.transform = sk === 0 ? "" : "skewY(" + sk.toFixed(2) + "deg)";
      }
      requestAnimationFrame(skewLoop);
    })(0); }
    window.addEventListener("scroll", function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------- scroll reveals */
  function getObserver() {
    if (io) return io;
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var t = en.target;
        t.style.opacity = "1";
        t.style.transform = "none";
        t.style.transformOrigin = "";   // hand pivot back to centre (for card tilt)
        t.style.filter = "none";
        t.dataset.guShown = "1";
        io.unobserve(t);
      });
    }, { threshold: 0.12, rootMargin: "0px 0px -6% 0px" });
    return io;
  }

  function initReveals() {
    var els = document.querySelectorAll("#guRoot [data-reveal]");
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (done.has(el)) continue;
      done.add(el);
      if (RM) { el.style.opacity = "1"; el.style.transform = "none"; continue; }
      var idx = el.parentElement
        ? Array.prototype.indexOf.call(el.parentElement.children, el) : 0;
      var delay = Math.min(idx * 90, 480);
      el.style.animation = "none";                 // override the load-fire keyframe
      el.style.opacity = "0";
      el.style.transformOrigin = "50% 100%";       // pivot from the floor — "rising on stage"
      el.style.transform = "perspective(1300px) rotateX(9deg) translateY(58px) scale(.965)";
      el.style.filter = FINE ? "blur(12px)" : "blur(6px)";
      el.style.transition =
        "opacity 1.05s cubic-bezier(.22,1,.36,1) " + delay + "ms," +
        "transform 1.15s cubic-bezier(.19,1,.22,1) " + delay + "ms," +
        "filter 1.05s ease " + delay + "ms";
      getObserver().observe(el);
    }
  }

  /* --------------------------------------------- hero: the opening number */
  function heroBox() {
    var h1 = document.querySelector("#guRoot header h1");
    return h1 ? h1.parentElement : null;
  }
  // Hide the hero copy so it can make an entrance once the curtain lifts.
  function prepHero() {
    if (staged || RM) return;
    var box = heroBox();
    if (!box || box.dataset.guHero) return;
    box.dataset.guHero = "1";
    Array.prototype.forEach.call(box.children, function (el) {
      el.style.opacity = "0";
      el.style.transform = "translateY(40px)";
      el.style.filter = "blur(8px)";
    });
  }
  // Cascade the hero copy in + push the backdrop image toward camera.
  function playHero() {
    var box = heroBox();
    if (!box) return;
    Array.prototype.forEach.call(box.children, function (el, i) {
      if (RM) { el.style.opacity = "1"; el.style.transform = "none"; el.style.filter = "none"; return; }
      var d = 140 + i * 150;
      el.style.transition =
        "opacity 1s cubic-bezier(.22,1,.36,1) " + d + "ms," +
        "transform 1.1s cubic-bezier(.19,1,.22,1) " + d + "ms," +
        "filter 1s ease " + d + "ms";
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          el.style.opacity = "1"; el.style.transform = "none"; el.style.filter = "none";
        });
      });
    });
    heroPushIn();
  }
  function heroPushIn() {
    var img = document.querySelector('#guRoot header img[data-parallax]');
    if (!img || RM || img.dataset.guZoom) return;
    img.dataset.guZoom = "1";
    img.style.animation = "guPushIn 2s cubic-bezier(.19,1,.22,1) both";
    // hand control back to the scroll-parallax (which keeps scale 1.05) when done
    setTimeout(function () { img.style.animation = "none"; img.style.transform = "scale(1.05)"; }, 2050);
  }
  // Raise the hero once — everything after this keeps it visible.
  function revealStage() {
    if (staged) return;
    staged = true;
    playHero();
  }

  /* ------------------------------------------------- the curtain-raise intro */
  function introSequence(onReveal) {
    // Reduced motion, or a repeat visit this session: skip the curtain, still
    // let the hero make its entrance.
    var seen = false;
    try { seen = !!sessionStorage.getItem("gu_intro"); } catch (e) {}
    if (RM || seen || !window.GUFFEE_HOME) { onReveal(); return; }
    try { sessionStorage.setItem("gu_intro", "1"); } catch (e) {}

    var wrap = document.createElement("div");
    wrap.id = "gu-intro";
    wrap.className = "gu-intro";
    wrap.innerHTML =
      '<div class="gu-curtain gu-curtain-l"></div>' +
      '<div class="gu-curtain gu-curtain-r"></div>' +
      '<div class="gu-flash" id="gu-flash"></div>' +
      '<div class="gu-intro-core">' +
        '<img class="gu-intro-logo" src="' + (window.GUFFEE_LOGO || '') + '" alt="Guffee">' +
        '<div class="gu-intro-tag">Now&nbsp;showing</div>' +
        '<div class="gu-intro-bar"><span></span></div>' +
      '</div>';
    document.body.appendChild(wrap);

    var start = Date.now(), exited = false;
    function exit() {
      if (exited) return;
      exited = true;
      wrap.classList.add("gu-intro-out");           // dim the title card
      setTimeout(function () {
        wrap.classList.add("gu-open");               // part the curtains
        var flash = document.getElementById("gu-flash");
        if (flash) flash.classList.add("gu-flash-go");
        onReveal();                                  // hero rises as they part
      }, 440);
      setTimeout(function () { if (wrap.parentNode) wrap.parentNode.removeChild(wrap); }, 1750);
    }
    // Lift the curtain once the real hero is rendered and a minimum beat has
    // passed — but never hang longer than the cap (covers the no-React case).
    var MIN = 1700, CAP = 3000;
    (function poll() {
      var ready = !!document.querySelector("#guRoot header h1");
      var t = Date.now() - start;
      if ((ready && t >= MIN) || t >= CAP) { exit(); return; }
      setTimeout(poll, 120);
    })();
  }

  /* ------------------------------------------------------ 3D tilt + glow on cards */
  var GLOW = {
    g4: ["rgba(255,138,30,.55)", "rgba(225,24,94,.55)", "rgba(123,47,242,.6)", "rgba(95,168,58,.55)"],
    g4f: ["rgba(255,138,30,.6)", "rgba(95,168,58,.55)", "rgba(225,24,94,.6)", "rgba(80,110,230,.6)"],
    b3: ["rgba(255,138,30,.5)", "rgba(225,24,94,.5)", "rgba(154,107,255,.55)"]
  };
  function wireTilt(sel, key) {
    var cards = document.querySelectorAll(sel);
    for (var i = 0; i < cards.length; i++) {
      (function (card, n) {
        if (card.dataset.guTilt) return;
        card.dataset.guTilt = "1";
        card.classList.add("gu-tilt");
        var pal = GLOW[key] || GLOW.b3;
        card.style.setProperty("--gu-glow", pal[n % pal.length]);
        if (!FINE || RM) return;
        card.addEventListener("mousemove", function (e) {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          card.style.transition = "transform .08s ease";
          card.style.transform =
            "perspective(1000px) rotateX(" + (-py * 7).toFixed(2) +
            "deg) rotateY(" + (px * 9).toFixed(2) + "deg) translateY(-6px)";
          card.classList.add("gu-glow");
        });
        card.addEventListener("mouseleave", function () {
          card.style.transition = "transform .5s cubic-bezier(.22,1,.36,1)";
          card.style.transform = "none";
          card.classList.remove("gu-glow");
        });
      })(cards[i], i);
    }
  }
  function initTilt() {
    wireTilt('#guRoot [data-grid="g4"]>div', "g4");
    wireTilt('#guRoot [data-grid="g4f"]>div', "g4f");
    wireTilt('#guRoot [data-grid="b3"]>div', "b3");
  }

  /* ------------------------------------------------- magnetic + sheen buttons */
  function initButtons() {
    var magnetic = document.querySelectorAll(
      '#guRoot header a[href="#flavors"], #guRoot header a[href="#science"],' +
      '#guRoot nav button, #guRoot [data-grid="cta"] a, #guRoot [data-grid="g4f"] button'
    );
    for (var i = 0; i < magnetic.length; i++) {
      (function (b) {
        if (b.dataset.guBtn) return;
        b.dataset.guBtn = "1";
        b.classList.add("gu-btn");
        // sheen looks best on the solid/gradient buttons, not the outline one
        if (b.getAttribute("href") !== "#science") b.classList.add("gu-sheen");
        if (!FINE || RM) return;
        b.addEventListener("mousemove", function (e) {
          var r = b.getBoundingClientRect();
          var x = (e.clientX - r.left - r.width / 2) / r.width;
          var y = (e.clientY - r.top - r.height / 2) / r.height;
          b.style.transition = "transform .1s ease";
          b.style.transform = "translate(" + (x * 10).toFixed(1) + "px," + (y * 8).toFixed(1) + "px)";
        });
        b.addEventListener("mouseleave", function () {
          b.style.transition = "transform .45s cubic-bezier(.22,1,.36,1)";
          b.style.transform = "none";
        });
      })(magnetic[i]);
    }
  }

  /* --------------------------------------------------------------------- init */
  function init() {
    if (!document.getElementById("guRoot")) return;
    buildOverlays();
    initReveals();
    // Before the show opens: keep the hero hidden. After: make sure it's shown,
    // even if the framework has just re-rendered a fresh hero node.
    if (staged) playHero(); else prepHero();
    parEls = Array.prototype.slice.call(document.querySelectorAll("#guRoot [data-parallax]"));
    scene3d = (RM || window.innerWidth <= 760) ? [] : Array.prototype.slice.call(document.querySelectorAll("#guRoot section"));
    bindScroll();
    initTilt();
    initButtons();
  }

  function boot() {
    init();
    introSequence(revealStage);
    // Re-run when the framework re-renders #guRoot (React boot / state changes).
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = null; init(); }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
